import clsx from "clsx";
import { useRef, type PointerEvent } from "react";
import {
  chartNumber,
  linearScale,
  logDomain,
  logScale,
  logTicks,
  niceDomain,
  niceTicks,
  useElementWidth,
  type ChartNumber,
} from "./chartMath";
import styles from "./charts.module.css";

export interface LinePoint {
  x: number;
  y: ChartNumber;
  /** False: the line runs through without a dot (a value carried forward to when it was last seen). */
  marker?: boolean;
}

export interface LineSeries {
  id: string;
  /** Labelled directly at the end of the line (color is never the only key). */
  label: string;
  points: ReadonlyArray<LinePoint>;
  /** Dashed stroke: a comparison series (a previous version, a target), or a second round of the palette. */
  dashed?: boolean;
  /** Palette slot 1–6 (`--color-chart-n`) to color series by group; without it series take the slots in order. */
  color?: number;
}

/** The series under the pointer and the point in effect there, as an index into its `points`. */
export interface PointedValue {
  seriesId: string;
  pointIndex: number;
}

export type LineCurve = "linear" | "step";
export type AxisScale = "linear" | "log";

export interface LineChartProps {
  series: readonly LineSeries[];
  /** Describes what the chart shows, for screen readers. */
  ariaLabel: string;
  /** Height in px (the width follows the container). */
  height?: number;
  formatX?: (x: number) => string;
  /** `step`, for showing as many decimals as the ticks need: their spacing, or on a log axis the tick itself. */
  formatY?: (y: number, step: number) => string;
  /** A shared x range, so small multiples line up (default: this chart's own points). */
  xDomain?: [number, number];
  yDomain?: [number, number];
  /** Include zero on the y axis (default true; a log axis has none). */
  includeZero?: boolean;
  /** "log": values that span orders of magnitude (prices per gram from samples to kilos); only y > 0 is drawn. */
  yScale?: AxisScale;
  /** "step": each value holds until the next point (prices); "linear" (default) joins the points. */
  curve?: LineCurve;
  /** Label every line at its end (default true); turn off when there are more lines than room. */
  endLabels?: boolean;
  /** Drawn on top, with every other series dimmed. */
  activeSeriesId?: string | null;
  /** The pointer moved onto a series (null: off every line). */
  onPointedChange?: (pointed: PointedValue | null) => void;
  className?: string;
}

const MARGIN = { top: 12, right: 96, bottom: 28, left: 48 };
/** Right margin without end labels, and the widest one end labels get. */
const PLAIN_RIGHT = 16;
const MAX_LABEL_RIGHT = 220;
/** Approximate advance of a character at --text-xs: tick labels (tabular figures) and end labels. */
const TICK_CHAR_WIDTH = 7;
const LABEL_CHAR_WIDTH = 6.5;
/** Minimum vertical distance between direct labels at the line ends. */
const LABEL_GAP = 14;
/** Pixels per tick a log axis needs before it shows only the powers of ten. */
const LOG_TICK_SPACING = 36;
/** Palette slots (`--color-chart-1`…`6`); series without a `color` beyond these aren't drawn. */
const PALETTE_SIZE = 6;

/** Nudges end labels apart (keeping their order) so series that finish close together stay readable. */
export function spreadLabels(positions: number[], min: number, max: number, gap = LABEL_GAP): number[] {
  const order = positions.map((y, index) => ({ y, index })).sort((a, b) => a.y - b.y);
  for (let i = 1; i < order.length; i += 1) {
    const previous = order[i - 1]!;
    const current = order[i]!;
    if (current.y - previous.y < gap) current.y = previous.y + gap;
  }
  const overflow = (order[order.length - 1]?.y ?? max) - max;
  if (overflow > 0) for (const item of order) item.y = Math.max(min, item.y - overflow);
  const result = new Array<number>(positions.length);
  for (const item of order) result[item.index] = item.y;
  return result;
}

/** SVG path through screen points; "step" holds each y until the next point's x. */
export function linePath(points: ReadonlyArray<{ x: number; y: number }>, curve: LineCurve = "linear"): string {
  return points
    .map((point, index) => {
      const x = point.x.toFixed(1);
      const y = point.y.toFixed(1);
      if (index === 0) return `M${x},${y}`;
      return curve === "step" ? `H${x}V${y}` : `L${x},${y}`;
    })
    .join("");
}

/**
 * The marked point in effect at `x`: on a step line the last one at or before it (the first before
 * the line starts), on a linear line the nearest. Null when the series has no marked points.
 */
export function pointIndexAt(points: ReadonlyArray<LinePoint>, x: number, curve: LineCurve = "linear"): number | null {
  let found: number | null = null;
  let first: number | null = null;
  points.forEach((point, index) => {
    if (point.marker === false || chartNumber(point.y) === null) return;
    first ??= index;
    if (curve === "step") {
      if (point.x <= x) found = index;
    } else if (found === null || Math.abs(point.x - x) < Math.abs(points[found]!.x - x)) {
      found = index;
    }
  });
  return found ?? first;
}

/** Lines over a numeric x axis (price history, evaporation timeline). Series use --color-chart-1…6. */
export function LineChart({
  series,
  ariaLabel,
  height = 240,
  formatX = String,
  formatY = String,
  xDomain,
  yDomain,
  includeZero = true,
  yScale = "linear",
  curve = "linear",
  endLabels = true,
  activeSeriesId = null,
  onPointedChange,
  className,
}: LineChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const width = useElementWidth(containerRef);
  const log = yScale === "log";
  const drawable = (y: number | null): y is number => y !== null && (!log || y > 0);
  const allPoints = series.flatMap((s) => s.points.map((p) => ({ x: p.x, y: chartNumber(p.y) })));
  const xs = allPoints.map((p) => p.x);
  const ys = allPoints.flatMap((p) => (drawable(p.y) ? [p.y] : []));
  const [x0, x1] = xDomain ?? (xs.length ? [Math.min(...xs), Math.max(...xs)] : [0, 1]);
  const [y0, y1] = yDomain ?? (log ? logDomain(ys) : niceDomain(ys, { includeZero }));
  const plotHeight = height - MARGIN.top - MARGIN.bottom;
  const yTicks = log ? logTicks(y0, y1, Math.max(4, Math.floor(plotHeight / LOG_TICK_SPACING))) : niceTicks(y0, y1, 4);
  const yStep = log ? (yTicks[0] ?? y0) : yTicks.length > 1 ? yTicks[1]! - yTicks[0]! : Math.abs(y1 - y0) || 1;
  const yLabels = yTicks.map((tick) => formatY(tick, log ? tick : yStep));
  const left = Math.max(MARGIN.left, Math.max(0, ...yLabels.map((label) => label.length)) * TICK_CHAR_WIDTH + 12);
  const labelChars = Math.max(0, ...series.map((s) => s.label.length));
  const right = endLabels
    ? Math.min(MAX_LABEL_RIGHT, Math.max(MARGIN.right / 2, labelChars * LABEL_CHAR_WIDTH + 14))
    : PLAIN_RIGHT;
  const plotRight = Math.max(width - right, left + 1);
  const plotBottom = MARGIN.top + plotHeight;
  const xSpan: [number, number] = [x0, x1 === x0 ? x0 + 1 : x1];
  const scaleX = linearScale(xSpan, [left, plotRight]);
  const scaleY = (log ? logScale : linearScale)([y0, y1], [plotBottom, MARGIN.top]);
  const xTicks = niceTicks(x0, x1, Math.max(2, Math.floor((plotRight - left) / 120)))
    .map((tick) => ({ tick, label: formatX(tick) }))
    .filter(({ label }, index, ticks) => index === 0 || label !== ticks[index - 1]!.label);
  const colored = series.every((s) => s.color !== undefined);
  const drawn = (colored ? series : series.slice(0, PALETTE_SIZE)).map((s, index) => ({
    series: s,
    slot: s.color ?? index + 1,
    points: s.points.flatMap((p) => {
      const y = chartNumber(p.y);
      return drawable(y) ? [{ x: p.x, y, marker: p.marker !== false }] : [];
    }),
  }));
  const ends = drawn.map(({ points }) => points[points.length - 1]);
  const labelY = spreadLabels(
    ends.map((end) => (end ? scaleY(end.y) : 0)),
    MARGIN.top,
    plotBottom,
  );
  const active = drawn.some(({ series: s }) => s.id === activeSeriesId) ? activeSeriesId : null;
  const order = drawn.map((_, index) => index);
  if (active !== null)
    order.sort((a, b) => Number(drawn[a]!.series.id === active) - Number(drawn[b]!.series.id === active));

  function point(event: PointerEvent<SVGSVGElement>) {
    if (!onPointedChange) return;
    const target = (event.target as Element).closest("[data-series-id]");
    const found = target ? drawn.find(({ series: s }) => s.id === target.getAttribute("data-series-id")) : undefined;
    const rect = svgRef.current?.getBoundingClientRect();
    if (!found || !rect) {
      onPointedChange(null);
      return;
    }
    const x = xSpan[0] + ((event.clientX - rect.left - left) / (plotRight - left)) * (xSpan[1] - xSpan[0]);
    const pointIndex = pointIndexAt(found.series.points, x, curve);
    onPointedChange(pointIndex === null ? null : { seriesId: found.series.id, pointIndex });
  }

  return (
    <div ref={containerRef} className={clsx(styles.chart, className)}>
      <svg
        ref={svgRef}
        className={styles.svg}
        viewBox={`0 0 ${width} ${height}`}
        width={width}
        height={height}
        role="img"
        aria-label={ariaLabel}
        onPointerMove={onPointedChange ? point : undefined}
        onPointerDown={onPointedChange ? point : undefined}
        onPointerLeave={onPointedChange ? () => onPointedChange(null) : undefined}
      >
        <g aria-hidden="true">
          {yTicks.map((tick, index) => (
            <g key={`y${tick}`}>
              <line className={styles.grid} x1={left} x2={plotRight} y1={scaleY(tick)} y2={scaleY(tick)} />
              <text
                className={styles.axisText}
                x={left - 8}
                y={scaleY(tick)}
                textAnchor="end"
                dominantBaseline="middle"
              >
                {yLabels[index]}
              </text>
            </g>
          ))}
          {xTicks.map(({ tick, label }) => (
            <text key={`x${tick}`} className={styles.axisText} x={scaleX(tick)} y={height - 8} textAnchor="middle">
              {label}
            </text>
          ))}
          <line className={styles.axis} x1={left} x2={plotRight} y1={plotBottom} y2={plotBottom} />
        </g>
        {order.map((index) => {
          const { series: s, slot, points: defined } = drawn[index]!;
          const path = linePath(
            defined.map((p) => ({ x: scaleX(p.x), y: scaleY(p.y) })),
            curve,
          );
          const last = ends[index];
          return (
            <g
              key={s.id}
              className={styles.series}
              data-series={slot}
              data-series-id={s.id}
              data-dimmed={(active !== null && s.id !== active) || undefined}
            >
              {onPointedChange ? <path className={styles.hit} d={path} /> : null}
              <path className={styles.line} d={path} data-dashed={s.dashed || undefined} />
              {defined.map((p) =>
                p.marker ? (
                  <circle key={p.x} className={styles.point} cx={scaleX(p.x)} cy={scaleY(p.y)} r={2.5}>
                    <title>{`${s.label}: ${formatY(p.y, yStep)} at ${formatX(p.x)}`}</title>
                  </circle>
                ) : null,
              )}
              {endLabels && last ? (
                <text className={styles.seriesLabel} x={scaleX(last.x) + 8} y={labelY[index]} dominantBaseline="middle">
                  {s.label}
                </text>
              ) : null}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

/** A short sample of a series' line (its palette color and dash), for legends and readouts. */
export function SeriesSwatch({
  color = 1,
  dashed = false,
  className,
}: {
  color?: number;
  dashed?: boolean;
  className?: string;
}) {
  return (
    <svg className={clsx(styles.swatch, className)} viewBox="0 0 20 8" aria-hidden="true" focusable="false">
      <g className={styles.series} data-series={color}>
        <line className={styles.line} x1={2} x2={18} y1={4} y2={4} data-dashed={dashed || undefined} />
      </g>
    </svg>
  );
}
